import clsx from "clsx";
import { Link } from "gatsby";
import React, { FC } from "react";

import { removeStyleFromHtml } from "../../../utils/styles";
import { Button } from "../../buttons/button";
import { Pill } from "../../forms";
import { TagColors, TagDimensions } from "../../forms/elements/tags/types";
import { EventTypeTags } from "../event-type-tags";
import { materialsMap } from "../materials-map";
import { Event, EventsLabels } from "../types";
import { useEventDates } from "../use-event-dates";

export interface EventCardProps {
  event: Event;
  labels: EventsLabels;
  pageLink: string | null;
}

export const EventCard: FC<EventCardProps> = ({ event, labels, pageLink }) => {
  const { formattedDateRange, formattedDateTimeRange } = useEventDates(event);

  const { country, location, eventType, materials } = event;

  return (
    <div className="flex flex-col bg-light border border-primary-light rounded-lg overflow-hidden">
      <div className="flex flex-col md:flex-row">
        <div
          className={clsx(
            "flex-1 flex flex-row items-center gap-2",
            "px-5 py-2 bg-primary text-white text-2xl leading-tight"
          )}
        >
          {formattedDateRange.toUpperCase()}
        </div>
        <div className="flex-1 flex flex-col gap-2 justify-center bg-primary-light px-5 py-2">
          <EventTypeTags eventType={eventType} labels={labels} />
          <div className="text-sm font-light">{formattedDateTimeRange}</div>
        </div>
      </div>
      <div className="flex-1 flex flex-col gap-4 px-5 py-4">
        {materials.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {event.materials.map(({ Name }) => {
              const materialData = materialsMap[Name];
              return (
                <Pill
                  color={materialData?.tagColor ?? TagColors.Neutral}
                  key={Name}
                  checked
                  readOnly
                  dimension={TagDimensions.Small}
                  className="w-fit"
                >
                  {labels[materialData?.label] ?? Name}
                </Pill>
              );
            })}
          </div>
        )}
        {/* Event title */}
        <h3 className="text-2xl font-medium">{event.title}</h3>

        {/* Event description (2 lines) */}
        {event.eventDescription && (
          <div
            className="text-sm lg:text-base font-light line-clamp-4 md:line-clamp-2 html-content"
            dangerouslySetInnerHTML={{ __html: removeStyleFromHtml(event.eventDescription) }}
          />
        )}

        {/* Event location and country */}
        {(event.country || event.location) && (
          <div className="text-sm lg:text-base font-light">
            <span className="font-medium">{labels["event-location"]}:</span>{" "}
            {[country, location].filter(Boolean).join(", ")}
          </div>
        )}
        <div className="flex justify-end mt-auto">
          <Link to={pageLink ?? ""}>
            <Button variant="primary" size="sm">
              {labels["event-go-to-details"]}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
